import * as React from "react"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { BellRing, Save } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MemberShell } from "@/components/member-shell"
import {
  getAlertPreferencesFn,
  saveAlertPreferencesFn,
} from "@/lib/app.functions"
import { PROPERTY_TYPES, REGIONS } from "@/lib/constants"
import { requireMemberRoute } from "@/lib/route-guards"

export const Route = createFileRoute("/alerts")({
  loader: async () => {
    await requireMemberRoute("/alerts")
    return getAlertPreferencesFn()
  },
  head: () => ({
    meta: [{ title: "Property alerts · Harbor & Pine Realty" }],
  }),
  component: AlertsPage,
})

function AlertsPage() {
  const { user, preferences } = Route.useLoaderData()
  const save = useServerFn(saveAlertPreferencesFn)
  const router = useRouter()
  const [pending, setPending] = React.useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    const form = new FormData(event.currentTarget)
    const optionalNumber = (key: string) => {
      const value = String(form.get(key) ?? "")
      return value ? Number(value) : undefined
    }
    try {
      await save({
        data: {
          enabled: form.get("enabled") === "on",
          frequency: String(form.get("frequency")) as
            "daily" | "weekly" | "monthly",
          filters: {
            transaction:
              form.get("transaction") === "rent"
                ? "rent"
                : form.get("transaction") === "buy"
                  ? "buy"
                  : undefined,
            type:
              form.get("type") && form.get("type") !== "all"
                ? (String(form.get("type")) as
                    | "residential"
                    | "commercial"
                    | "land"
                    | "investment"
                    | "holiday-rental")
                : undefined,
            region: String(form.get("region") ?? "") || undefined,
            minPrice: optionalNumber("minPrice"),
            maxPrice: optionalNumber("maxPrice"),
            bedrooms: optionalNumber("bedrooms"),
          },
        },
      })
      await router.invalidate()
      toast.success("Alert preferences saved.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to save.")
    } finally {
      setPending(false)
    }
  }

  return (
    <MemberShell
      user={user}
      eyebrow="Saved filters · notification preferences"
      title="Property alerts"
      intro="This demo persists alert criteria and frequency but sends no external email."
    >
      <form onSubmit={handleSubmit} className="max-w-3xl">
        <div className="flex items-start gap-4 border-2 border-foreground bg-accent p-5">
          <BellRing className="mt-1 size-6" />
          <div className="flex-1">
            <label className="flex cursor-pointer items-center gap-3 font-heading text-xl font-bold uppercase">
              <Checkbox name="enabled" defaultChecked={preferences.enabled} />
              Enable property alerts
            </label>
            <p className="mt-2 text-sm text-foreground/70">
              Saved only for workflow demonstration; no notifications leave the
              application.
            </p>
          </div>
        </div>
        <fieldset className="mt-6 grid gap-5 border bg-white p-5 sm:grid-cols-2">
          <legend className="field-label bg-background px-2">
            Alert criteria
          </legend>
          <AlertField label="Frequency" htmlFor="frequency">
            <select
              id="frequency"
              name="frequency"
              defaultValue={preferences.frequency}
              className="field-input"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </AlertField>
          <AlertField label="Looking to" htmlFor="transaction">
            <select
              id="transaction"
              name="transaction"
              defaultValue={preferences.filters.transaction ?? ""}
              className="field-input"
            >
              <option value="">Buy or rent</option>
              <option value="buy">Buy</option>
              <option value="rent">Rent</option>
            </select>
          </AlertField>
          <AlertField label="Property type" htmlFor="type">
            <select
              id="type"
              name="type"
              defaultValue={preferences.filters.type ?? "all"}
              className="field-input"
            >
              <option value="all">All types</option>
              {PROPERTY_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </AlertField>
          <AlertField label="Region" htmlFor="region">
            <select
              id="region"
              name="region"
              defaultValue={preferences.filters.region ?? ""}
              className="field-input"
            >
              <option value="">All regions</option>
              {REGIONS.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </AlertField>
          {[
            ["Minimum price", "minPrice", preferences.filters.minPrice],
            ["Maximum price", "maxPrice", preferences.filters.maxPrice],
            ["Minimum bedrooms", "bedrooms", preferences.filters.bedrooms],
          ].map(([label, name, value]) => (
            <AlertField
              key={String(name)}
              label={String(label)}
              htmlFor={String(name)}
            >
              <Input
                id={String(name)}
                name={String(name)}
                type="number"
                min="0"
                defaultValue={value as number | undefined}
                className="field-input"
              />
            </AlertField>
          ))}
        </fieldset>
        <Button
          type="submit"
          className="field-button mt-6"
          size="lg"
          disabled={pending}
        >
          <Save />
          {pending ? "Saving…" : "Save alert"}
        </Button>
      </form>
    </MemberShell>
  )
}

function AlertField({
  label,
  htmlFor,
  children,
}: {
  label: string
  htmlFor: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="field-label">
        {label}
      </Label>
      {children}
    </div>
  )
}
